import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSnapshot } from "valtio";
import { registrationStore } from "@/features/auth/store";
import { BusinessSchema, TBusinessSchema } from "@/features/auth/forms/schema";
import { useUserRegister, useVerifyEmail } from "@/features/auth/repositories/auth-repository";
import { EMAIL_VERIFICATION_ENABLED, buildRegistrationFormData } from "@/features/auth/registration";
import { MapPinIcon } from "@/components/icons";
import { regions } from "@/constants/regions";

import useGeoLocation from "@/hooks/useGeoLocation";
import PhoneNumberInput from "@/components/form-controls/PhoneInput";
import SelectInput from "@/components/form-controls/SelectInput";
import TextInput from "@/components/form-controls/TextInput";
import RegisterHeader from "@/features/auth/fragments/registration-header";
import SubmitButton from "@/features/auth/forms/submit-button";
import Label from "@/components/form-controls/Label";
import IconButton from "@/components/buttons/IconButton";




const RegisterBusinessForm = () => {
    const navigate = useNavigate();
    const store = useSnapshot(registrationStore);
    
    const { location, getLocation, isLoading } = useGeoLocation();

    const { register, handleSubmit, watch, setValue, control, formState: { errors }} = useForm({
        resolver: yupResolver(BusinessSchema),
        defaultValues: store.business ? store.business: undefined
    })

    const getRegions = regions.map((el) => ({label: el.name , value: el.name}))

    const getDistricts = (name?: string) => {
        let region = regions.find(el => el.name == name)
        
        if(region) return region.districts.map((el) => ({label: el.name , value: el.name}))

        return []
    }

    const { mutate, isPending } = useVerifyEmail(
        () => navigate("/auth/register/verification")
    );

    // With email verification off, this is the last data-entry step, so registration is
    // submitted here and the user goes straight to the welcome screen.
    const { mutate: registerUser, isPending: isRegistering } = useUserRegister(
        () => navigate("/auth/register/welcome")
    );

    const submit = async (data: TBusinessSchema) => {
        store.setBusiness(data);

        if (!EMAIL_VERIFICATION_ENABLED) {
            const formData = await buildRegistrationFormData({
                personal: store.personal,
                identification: store.identification,
                business: data
            });
            registerUser(formData);
            return;
        }

        store.personal && mutate(store.personal?.email);
    }

    useEffect(() => {
        if(location) {
            setValue("latitude", location.coords.latitude.toString());
            setValue("longitude", location.coords.longitude.toString());
        }
    }, [location, isLoading])

    return (
        <div className="space-y-12">
            <RegisterHeader 
                heading={"Provide Your Business Details"}
                description={"Please enter your business details if any."}
            />

            <form className="grid grid-cols-2 gap-4" onSubmit={handleSubmit(submit)}>
                <TextInput 
                    label="Business Name"
                    placeholder="enter your registered business name"
                    error={errors.businessName?.message?.toString()}
                    register={register("businessName")}
                    required
                />

                <TextInput 
                    label="Trading Name"
                    placeholder="enter your trading name"
                    error={errors.tradingName?.message?.toString()}
                    register={register("tradingName")}
                />

                <TextInput 
                    label="TIN Number"
                    placeholder="enter your tin number"
                    className="col-span-2"
                    error={errors.tinNumber?.message?.toString()}
                    register={register("tinNumber")}
                />

                <SelectInput 
                    label="Region"
                    displayName="label"
                    valueName="value"
                    options={getRegions}
                    error={errors.region?.message?.toString()}
                    register={register("region")}
                    required
                />

                <SelectInput 
                    label="District"
                    displayName="label"
                    valueName="value"
                    options={getDistricts(watch("region"))}
                    error={errors.district?.message?.toString()}
                    register={register("district")}
                    required
                />

                <TextInput 
                    label="Street"
                    placeholder="enter your trading name"
                    error={errors.street?.message?.toString()}
                    register={register("street")}
                />

                <div className="">
                    <div className="flex justify-between items-center mb-0.5">
                        <Label text="GPS code" />

                        <IconButton 
                            type="button" 
                            intent="primary"
                            onClick={getLocation} 
                            loading={isLoading}
                        >
                            <MapPinIcon className="size-4 text-primary" />
                        </IconButton>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <TextInput 
                            type="text"
                            placeholder="lat"
                            error={errors.latitude?.message?.toString()}
                            register={register("latitude")}
                        />

                        <TextInput 
                            type="text"
                            placeholder="lng"
                            error={errors.longitude?.message?.toString()}
                            register={register("longitude")}
                        /> 
                    </div>
                </div>

                <h4 className="text-secondary font-semibold col-span-2 pt-6">
                    Contact Information
                </h4>

                <TextInput 
                    label="Manager Name"
                    placeholder="e.g. Peter Mosha"
                    className="col-span-2"
                    error={errors.managerName?.message?.toString()}
                    register={register("managerName")}
                    required
                />

                <PhoneNumberInput 
                    name="managerPhone"
                    label="Phone Number"
                    placeholder="enter your tin number"
                    control={control}
                    required
                />

                <TextInput 
                    label="Email Address"
                    placeholder="e.g. mosha.peter@gmail.com"
                    type="email"
                    error={errors.managerEmail?.message?.toString()}
                    register={register("managerEmail")}
                    required
                />

                <SubmitButton 
                    hasPrev={true}
                    loading={isPending || isRegistering}
                    onPrev={() => navigate("/auth/register/identity")}
                />
            </form>
        </div>
    )
}

export default RegisterBusinessForm
